/**
 * Il dettaglio di una commessa, aperto dal Cruscotto a tre colonne (23-09-2026).
 *
 * Perché un drawer e non l'espansione in linea che c'è nel Portafoglio: nel
 * Cruscotto le commesse stanno in tre colonne strette, una accanto all'altra.
 * Espandere lì dentro vorrebbe dire far crescere una colonna e disallineare le
 * altre due — e il confronto fra le tre colonne è tutto il senso della pagina.
 *
 * Contiene i due gesti che rendono il cruscotto operativo e che finora stavano
 * solo sulla riga del Portafoglio: **passare la commessa a Logistica** e
 * **anticiparla**. Sono le stesse chiamate (`POST /m3/pipeline`), non una copia.
 */
import { ArrowRight, CalendarCheck, StickyNote, Truck, X } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { AnticipaDialog } from "@/components/m1/AnticipaDialog";
import { DoveLaMerceFotografia } from "@/components/m1/DoveLaMerceBarra";
import { Skeleton } from "@/components/ui/skeleton";
import { useM1Commessa } from "@/hooks/useM1";
import { useM3CreaCommessaDaM1 } from "@/hooks/useM3";
import { num } from "@/lib/numeri";
import { cn } from "@/lib/utils";
import type { M1OrdineRaggruppato, M1OrdineRiga } from "@/types/api";

const fmt = (v: string | number) => num(Number(v));

/** Quanto di questa commessa è già uscito, secondo il gestionale: `qty_spedita`
 *  su `qty_ordinata`. È il numero che nessuno può contestare, ed è quello che
 *  spiega l'apparente contraddizione «uscita, però pronta». */
function quotaUscita(ord: M1OrdineRaggruppato): number {
  const tot = Number(ord.qty_ordinata_totale) || 1;
  return Math.round((100 * Number(ord.dove_la_merce.ritirato)) / tot);
}

const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

function dataBreve(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [, m, g] = iso.split("T")[0].split("-").map(Number);
  return `${g} ${MESI[m - 1]}`;
}

/** Da dove viene la merce di questa riga, in una parola.
 *  Stessa grammatica del drawer dell'articolo: chi ha imparato a leggere l'uno
 *  non deve reimparare per l'altro. */
function copertura(r: M1OrdineRiga): { testo: string; tono: string; titolo: string } {
  if (r.stato_maitime === null) {
    return {
      testo: "non valutata",
      tono: "bg-muted text-muted-foreground",
      titolo: "L'articolo è marcato «materiale di consumo»: il motore non lo valuta",
    };
  }
  const manca = Number(r.gap_qty_maitime ?? 0);
  if (manca > 0) {
    return {
      testo: `manca ${fmt(manca)}`,
      tono: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300",
      titolo: r.causale_maitime ?? "",
    };
  }
  if (r.stato_maitime === "01 - EVADIBILE IN RITARDO") {
    return {
      testo: "coperta, tardi",
      tono: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
      titolo: r.causale_maitime ?? "",
    };
  }
  return {
    testo: "coperta",
    tono: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
    titolo: r.causale_maitime ?? "",
  };
}

export function CommessaDrawer({
  numero,
  onClose,
}: {
  numero: number | null;
  onClose: () => void;
}) {
  const { data: ord, isLoading } = useM1Commessa(numero);

  if (numero === null) return null;

  return (
    <div className="fixed inset-0 top-14 z-40 flex justify-end" role="dialog">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden />
      <div className="relative z-10 flex h-full w-full max-w-2xl flex-col overflow-y-auto border-l bg-background shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b px-4 py-3">
          <div className="min-w-0">
            <h3 className="font-mono text-[13px] font-semibold">
              Commessa #{numero}
              {ord?.riferimento_cliente && (
                <span className="ml-2 font-sans text-[11.5px] font-normal text-muted-foreground">
                  rif. cliente {ord.riferimento_cliente}
                </span>
              )}
            </h3>
            <p className="truncate text-[11.5px] text-muted-foreground">
              {ord?.cliente_ragione_sociale ?? "—"}
              {ord?.cliente_citta ? ` · ${ord.cliente_citta}` : ""}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X className="size-4" />
          </button>
        </div>

        {isLoading || !ord ? (
          <div className="p-4">
            <Skeleton className="h-80 w-full" />
          </div>
        ) : (
          <Corpo ord={ord} onClose={onClose} />
        )}
      </div>
    </div>
  );
}

function Corpo({ ord, onClose }: { ord: M1OrdineRaggruppato; onClose: () => void }) {
  const navigate = useNavigate();
  // ⚠️ Non più `pipeline-per-ordini`: quello legge `m3.carico` + assegnazioni,
  // che in tutto il database contiene **2 commesse**, quindi il badge «a
  // Logistica» era praticamente sempre spento. Il tabellone vero di Marina è
  // `avviso_ritiro`, e arriva qui dentro come `impegno` (23-09-2026).
  const imp = ord.impegno;
  const impegnata = !!imp && imp.n_carichi_vivi > 0;
  const creaCommessa = useM3CreaCommessaDaM1();
  const [anticipaAperto, setAnticipaAperto] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const m = ord.dove_la_merce;
  const manca = Number(m.da_produrre);
  const pronta = Number(m.magazzino);
  const inProduzione = Number(m.produzione);

  function passaALogistica() {
    setErrore(null);
    creaCommessa.mutate(
      { numero_ordine: ord.numero_ordine, pipeline_tipo: "effettiva" },
      {
        onError: (err: unknown) =>
          setErrore(err instanceof Error ? err.message : String(err)),
      },
    );
  }

  return (
    <div className="flex-1 space-y-4 p-4">
      {/* La frase che risponde alla domanda con cui hai cliccato, prima dei
          numeri: i numeri servono a verificarla, non a ricavarla. */}
      <p className="text-[13px] leading-snug">
        {manca > 0 ? (
          <>
            Di <b>{fmt(ord.qty_residua_totale)}</b> pezzi ancora da consegnare,{" "}
            <b className="text-rose-700 dark:text-rose-400">{fmt(manca)} non esistono</b> né a
            magazzino né nel piano di produzione: finché non vengono pianificati questa
            commessa non ha una data.
          </>
        ) : inProduzione > 0 ? (
          <>
            Tutti i <b>{fmt(ord.qty_residua_totale)}</b> pezzi residui sono coperti:{" "}
            <b>{fmt(pronta)}</b> già a magazzino e <b>{fmt(inProduzione)}</b> dal piano di
            produzione. Sarà completa il <b>{dataBreve(dataPronta(ord))}</b>.
          </>
        ) : (
          <>
            Tutti i <b>{fmt(ord.qty_residua_totale)}</b> pezzi residui sono{" "}
            <b className="text-emerald-700 dark:text-emerald-400">pronti a magazzino</b>:
            questa commessa può partire oggi.
          </>
        )}
      </p>

      <DoveLaMerceFotografia merce={ord.dove_la_merce} titolo="Dov'è la merce di questa commessa" />

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Numero label="Residui" valore={ord.qty_residua_totale} sotto={`su ${fmt(ord.qty_ordinata_totale)} ordinati`} />
        <Numero
          label="Righe aperte"
          valore={ord.n_righe_aperte}
          sotto={`su ${ord.n_righe} ${ord.n_righe === 1 ? "riga" : "righe"}`}
        />
        <Numero label="Consegna" testo={dataBreve(ord.data_consegna_minima)} sotto="data del gestionale" />
        {/* Si chiamava «Impegno», come la data in cui la commessa è entrata. Ma
            adesso in questo stesso pannello «impegno» è il carico che se l'è
            presa: due significati a venti centimetri l'uno dall'altro. */}
        <Numero
          label="In portafoglio dal"
          testo={dataBreve(ord.data_impegno_minima)}
          sotto="data di impegno"
        />
      </div>

      {impegnata && (
        /* Il pezzo che evita l'informazione sbagliata: questa merce risulta
           pronta, ma qualcuno se l'è già presa. */
        <div
          className={cn(
            "rounded-lg border px-3 py-2 text-[12px]",
            imp!.vale === "lettura"
              ? "border-border bg-muted/40"
              : "border-sky-300 bg-sky-50 dark:border-sky-900 dark:bg-sky-950",
          )}
        >
          <p className="flex items-center gap-1.5 font-medium">
            <Truck className="size-3.5" />
            {imp!.ha_ndp
              ? `Nota di prelievo ${imp!.ndp_numero ?? ""} già emessa`.trim()
              : `Carico previsto in Spedizioni — ${imp!.stato.replace(/_/g, " ")}`}
          </p>
          {/* Da dove viene l'attribuzione. Una riga che dichiara la fonte o è
              giusta, o dice subito dove andare a controllare — ed è la
              differenza fra una pagina discutibile e una verificabile. */}
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {imp!.vale === "manuale"
              ? "Attribuzione confermata a mano da Logistica."
              : imp!.vale === "documento"
                ? "Il numero di questa commessa è stampato sulla nota di prelievo."
                : "⚠️ Attribuzione ricavata da un'email, non confermata da un documento."}
          </p>
          <p className="mt-0.5 text-muted-foreground">
            {imp!.n_carichi_vivi > 1
              ? `${imp!.n_carichi_vivi} carichi aperti per questa commessa · `
              : ""}
            ritiro{" "}
            {imp!.data_ritiro ? (
              <b className="text-foreground">{dataBreve(imp!.data_ritiro)}</b>
            ) : (
              /* 26 carichi su 27 stanno così: da oggi Aida lo chiede a Marina. */
              <i>non ancora fissato — Aida l&apos;ha chiesto a Marina</i>
            )}
            {imp!.pronto || imp!.pronto_dal ? (
              <> · pronto {imp!.pronto_dal ? dataBreve(imp!.pronto_dal) : "sì"}</>
            ) : (
              <> · «pronto» non segnato</>
            )}
            {imp!.pallet ? <> · {fmt(imp!.pallet)} pallet</> : null}
          </p>
        </div>
      )}

      {!impegnata && imp?.uscito_il && (
        /* Non è un impegno: è il motivo per cui la commessa è ancora aperta.
           E il numero che regge la frase è quello del **gestionale**, non la
           data: «è uscito il 75%» è il loro dato e non si discute. */
        <p className="text-[11.5px] text-muted-foreground">
          Di questa commessa è <b>già uscito il {quotaUscita(ord)}%</b> — {fmt(m.ritirato)} pezzi
          su {fmt(ord.qty_ordinata_totale)}. L&apos;ultima uscita è del{" "}
          <b>{dataBreve(imp.uscito_il)}</b>
          {imp.ndp_uscita ? (
            <>
              {" "}
              con la nota di prelievo <b>{imp.ndp_uscita}</b>
            </>
          ) : null}
          . Quello che resta non ha nessun ritiro fissato.
        </p>
      )}

      {/* L'asse del ritiro, che è indipendente dallo stato della merce.
          ⚠️ Quando un carico vivo se l'è già presa, del ritiro parla il riquadro
          qui sopra e basta: `ritiro_fissato_il` è la data più vicina fra **tutti**
          gli avvisi che citano la commessa, compresi quelli partiti, e sulla #121
          finiva sotto «ritiro non ancora fissato» un «Ritiro fissato il 17 set»
          che era la data del carico uscito. Due righe che si contraddicono a tre
          centimetri di distanza. */}
      <div className="flex flex-wrap items-center gap-2 text-[11.5px]">
        {ord.stato_ritiro === "tassativa" && !impegnata && !imp?.uscito_il ? (
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium",
              ord.ritiro_scaduto
                ? "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300"
                : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
            )}
          >
            <CalendarCheck className="size-3" />
            {/* Senza data il ritiro è comunque fissato — NDP emessa col campo
                vuoto, 31 avvisi su 59: «il ... —» si leggeva come un dato rotto. */}
            {ord.ritiro_fissato_il
              ? `Ritiro ${ord.ritiro_scaduto ? "scaduto" : "fissato"} il ${dataBreve(ord.ritiro_fissato_il)}`
              : "Ritiro fissato, senza data sull'avviso"}
          </span>
        ) : ord.stato_ritiro === "a_chiamata" ? (
          <span className="rounded-full bg-orange-100 px-2 py-0.5 font-medium text-orange-900 dark:bg-orange-950 dark:text-orange-300">
            A chiamata — ritira quando gli serve
          </span>
        ) : (
          <span className="text-muted-foreground">Ritiro non ancora fissato</span>
        )}
        {ord.note_memoria > 0 && (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <StickyNote className="size-3" />
            {ord.note_memoria} note dei commerciali
          </span>
        )}
      </div>

      <div>
        <p className="mb-1 text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground">
          le righe della commessa
        </p>
        <div className="overflow-hidden rounded-lg border">
          <table className="w-full text-[12px]">
            <thead className="bg-muted/40 text-[10px] text-muted-foreground">
              <tr>
                <th className="px-2.5 py-1.5 text-left font-medium">Articolo</th>
                <th className="px-2.5 py-1.5 text-right font-medium">Residua</th>
                <th className="px-2.5 py-1.5 text-left font-medium">Copertura</th>
                <th className="px-2.5 py-1.5 text-left font-medium">Pronta</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {ord.righe.map((r) => {
                const cop = copertura(r);
                return (
                  <tr key={r.id}>
                    <td className="px-2.5 py-1.5">
                      <div className="font-mono text-[11px] font-semibold">
                        {r.codice_articolo}
                      </div>
                      <div className="truncate text-[10px] text-muted-foreground">
                        {r.descrizione_articolo ?? "—"}
                      </div>
                    </td>
                    <td className="px-2.5 py-1.5 text-right font-semibold tabular-nums">
                      {fmt(r.qty_residua)}
                    </td>
                    <td className="px-2.5 py-1.5">
                      <span
                        className={cn(
                          "inline-block whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10.5px] font-medium",
                          cop.tono,
                        )}
                        title={cop.titolo}
                      >
                        {cop.testo}
                      </span>
                    </td>
                    <td className="px-2.5 py-1.5 tabular-nums text-muted-foreground">
                      {dataBreve(r.data_minima_evadibile)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {errore && (
        <p className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-[11.5px] text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          {errore}
        </p>
      )}

      {/* I gesti. `stato.in_pipeline` decide da solo cosa ha senso offrire: una
          commessa già in mano a Marina non si passa due volte, si apre. */}
      <div className="flex flex-wrap items-center gap-2 border-t pt-3">
        {impegnata ? (
          <>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-100 px-2 py-1 text-[11.5px] font-medium text-sky-900 dark:bg-sky-950 dark:text-sky-300">
              <Truck className="size-3.5" />
              Già su un carico previsto
            </span>
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate("/moduli/spedizioni?tab=carichi");
              }}
              className="inline-flex items-center gap-1.5 rounded border px-2.5 py-1.5 text-[11.5px] hover:bg-accent"
            >
              Apri in Logistica
              <ArrowRight className="size-3.5" />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={passaALogistica}
              disabled={creaCommessa.isPending}
              className="inline-flex items-center gap-1.5 rounded bg-foreground px-2.5 py-1.5 text-[11.5px] font-medium text-background hover:bg-foreground/90 disabled:opacity-50"
            >
              <Truck className="size-3.5" />
              {creaCommessa.isPending ? "Passaggio in corso…" : "Passa a Logistica"}
            </button>
            <button
              type="button"
              onClick={() => setAnticipaAperto(true)}
              className="inline-flex items-center gap-1.5 rounded border px-2.5 py-1.5 text-[11.5px] hover:bg-accent"
              title="Entra in pipeline come anticipo: il cliente viene pre-avvisato mentre si finisce di confezionare"
            >
              Anticipa
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => {
            onClose();
            navigate(
              `/moduli/order-readiness?tab=portafoglio&cliente=${ord.cliente_codice}`,
            );
          }}
          className="ml-auto inline-flex items-center gap-1 text-[11.5px] text-brand-700 hover:underline dark:text-brand-400"
        >
          Tutte le commesse del cliente
          <ArrowRight className="size-3" />
        </button>
      </div>

      <AnticipaDialog
        ordine={anticipaAperto ? ord : null}
        onClose={() => setAnticipaAperto(false)}
        isPending={creaCommessa.isPending}
        onConfirm={(data) => {
          setErrore(null);
          creaCommessa.mutate(
            {
              numero_ordine: ord.numero_ordine,
              pipeline_tipo: "anticipo",
              data_pronto_prevista: data,
            },
            {
              onSuccess: () => setAnticipaAperto(false),
              onError: (err: unknown) =>
                setErrore(err instanceof Error ? err.message : String(err)),
            },
          );
        }}
      />
    </div>
  );
}

/** La data in cui la commessa sarà completa: la più lontana fra le sue righe.
 *  Una commessa è pronta quando lo è la sua riga più lenta. */
function dataPronta(ord: M1OrdineRaggruppato): string | null {
  const date = ord.righe
    .filter((r) => Number(r.qty_residua) > 0 && r.data_minima_evadibile)
    .map((r) => r.data_minima_evadibile as string);
  return date.length ? date.reduce((a, b) => (a > b ? a : b)) : null;
}

function Numero({
  label,
  valore,
  testo,
  sotto,
}: {
  label: string;
  valore?: string | number;
  testo?: string;
  sotto: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-2.5">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold tabular-nums">
        {testo ?? (valore !== undefined ? fmt(valore) : "—")}
      </p>
      <p className="text-[10px] text-muted-foreground">{sotto}</p>
    </div>
  );
}
